import { useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useState } from "react";
import PreviousPageHeader from "../../components/PreviousPageHeader/PreviousPageHeader";
import { useEffect } from "react";
import axiosClient from "../../axiosClient";
import auth from "../../fire";
import { useAuthState } from "react-firebase-hooks/auth";
import TwoDigitInput from "../../components/TwoDigitInput/TwoDigitInput";
import RepRangeInput from "../../components/RepRangeInput/RepRangeInput";
import RestRangeInput from "../../components/RestRangeInput/RestRangeInput";
import TempoInput from "../../components/TempoInput/TempoInput";
import NotesInput from "../../components/NotesInput/NotesInput";
import ExerciseNameEditor from "../../components/ExerciseNameEditor/ExerciseNameEditor";
import MessageModal from "../../components/MessageModal/MessageModal";
// import RPERangeInput from "../../components/RPERangeInput/RPERangeInput";
const _ = require("lodash");

const ExerciseTemplate: React.FC = () => {
  const { programId, workoutTemplateId, exerciseTemplateId } = useParams() as {
    programId: string;
    workoutTemplateId: string;
    exerciseTemplateId: string;
  };
  const navigate = useNavigate();
  const [workoutTemplate, setWorkoutTemplate] =
    useState<WorkoutTemplate | null>(null);
  const [currentExerciseTemplate, setCurrentExerciseTemplate] =
    useState<ExerciseTemplate | null>(null);
  const [exerciseNameSuggestions, setExerciseNameSuggestions] = useState<
    String[]
  >([]);
  const [user, loading] = useAuthState(auth);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });

  const getWorkoutTemplate = useCallback(async () => {
    try {
      const getWorkoutTemplateResponse = await axiosClient.get(
        `/api/workout-templates/${workoutTemplateId}`
      );

      const workoutTemplate: WorkoutTemplate = getWorkoutTemplateResponse.data;

      const exerciseTemplate = workoutTemplate.exercises.find((exercise) => {
        return exercise._id === exerciseTemplateId;
      });

      setWorkoutTemplate(workoutTemplate);

      if (exerciseTemplate) {
        setCurrentExerciseTemplate(exerciseTemplate);
      } else {
        throw Error("Exercise Template not found in WorkoutTemplate");
      }

      setIsInitialLoading(false);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Fetching Workout Template",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(false);
    }
  }, [exerciseTemplateId, workoutTemplateId]);

  const getExerciseNameSuggestions = useCallback(async () => {
    try {
      const fetchExerciseResponse = await axiosClient.get(
        `/api/exercises/allExercisesDoneByUser`
      );

      setExerciseNameSuggestions(fetchExerciseResponse.data);
    } catch (error) {
      console.error("Error fetching previous exercise names:", error);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    getWorkoutTemplate();
    getExerciseNameSuggestions();
  }, [user, loading, getWorkoutTemplate, navigate, getExerciseNameSuggestions]);

  const updateWorkoutTemplateExercise = async (
    exerciseUpdate: DraftExerciseTemplate,
    errorModalTitle: string
  ) => {
    if (!workoutTemplate) return;

    setCurrentExerciseTemplate((prevExerciseTemplate) => {
      if (prevExerciseTemplate) {
        return {
          ...prevExerciseTemplate,
          ...exerciseUpdate,
        };
      }
      return null;
    });

    const updatedExercise = { ...currentExerciseTemplate, ...exerciseUpdate };

    const newExercisesArray = workoutTemplate.exercises.map((exercise) => {
      return exercise._id === exerciseTemplateId ? updatedExercise : exercise;
    });

    try {
      const updateExerciseResponse = await axiosClient.put(
        `/api/workout-templates/${workoutTemplateId}`,
        { exercises: newExercisesArray }
      );

      if (updateExerciseResponse.status !== 200) {
        throw new Error(updateExerciseResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: errorModalTitle,
        message: `${errorMessage} Please try again or contact support.`,
      });
    }
  };

  /* PUT Name */
  const handleExerciseNameSubmit = async (exerciseName: string) => {
    if (
      currentExerciseTemplate &&
      exerciseName.trim() === currentExerciseTemplate.name?.trim()
    )
      return;

    const exerciseChange = { name: exerciseName };

    updateWorkoutTemplateExercise(
      exerciseChange,
      "Error Updating Exercise Name"
    );
  };

  /* PUT Sets */
  const handleNumberOfSetsSubmit = async (numberOfSets: number) => {
    if (
      currentExerciseTemplate &&
      numberOfSets === currentExerciseTemplate.sets
    )
      return;

    const exerciseChange = { sets: numberOfSets };

    updateWorkoutTemplateExercise(exerciseChange, "Error Updating Sets");
  };

  /* PUT RepRange */
  const handleRepRangeSubmit = async (repRange: RepRange) => {
    if (
      currentExerciseTemplate &&
      _.isEqual(repRange, currentExerciseTemplate.repRange)
    )
      return;

    const exerciseChange = { repRange };

    updateWorkoutTemplateExercise(exerciseChange, "Error Updating Rep Range");
  };

  /* PUT Rest */
  const handleRestRangeSubmit = async (restRange: RestRange) => {
    if (
      currentExerciseTemplate &&
      _.isEqual(restRange, currentExerciseTemplate.rest)
    )
      return;

    const exerciseChange = { rest: restRange };

    updateWorkoutTemplateExercise(exerciseChange, "Error Updating Rest Range");
  };

  /* PUT Tempo */
  const handleTempoSubmit = async (tempo: Tempo) => {
    if (
      currentExerciseTemplate &&
      _.isEqual(tempo, currentExerciseTemplate.tempo)
    )
      return;

    const exerciseChange = { tempo };

    updateWorkoutTemplateExercise(exerciseChange, "Error Updating Tempo");
  };

  /* PUT Notes */
  const handleNotesSubmit = async (notes: string) => {
    if (
      currentExerciseTemplate &&
      notes.trim() === currentExerciseTemplate.notes?.trim()
    )
      return;

    const exerciseChange = { notes };

    updateWorkoutTemplateExercise(exerciseChange, "Error Updating Notes");
  };

  return (
    <div className="h-full relative">
      <PreviousPageHeader
        previousPageLink={`/programs/${programId}/workout-templates/${workoutTemplateId}`}
        pageTitle="Exercise Template"
      />
      <div
        className="overflow-y-scroll"
        style={{
          height: `calc(100vh - 63px  - 63px)`,
        }}
      >
        <p className="p-2 pb-0 text-xs font-regular text-gray-500">
          Add specific exercise details, like amount of sets & reps. These will
          appear in your workout log, so you'll know exactly what to do every
          exercise.
        </p>
        <div className="pt-4 px-2">
          <ExerciseNameEditor
            label="Exercise Name"
            exerciseName={
              currentExerciseTemplate ? currentExerciseTemplate.name : ""
            }
            onExerciseNameChange={handleExerciseNameSubmit}
            isLoading={isInitialLoading}
            exerciseNameSuggestions={exerciseNameSuggestions}
          />
        </div>
        <div className="pt-4 px-2 flex flex-row gap-x-4">
          <TwoDigitInput
            id="number-of-sets"
            label="Sets"
            initialValue={
              currentExerciseTemplate && currentExerciseTemplate.sets
                ? currentExerciseTemplate.sets
                : ""
            }
            className="w-[calc((100vw-16px-16px-14px-16px-20px)/3)]"
            onBlur={handleNumberOfSetsSubmit}
            isLoading={isInitialLoading}
          />
          <RepRangeInput
            minInitialValue={
              currentExerciseTemplate &&
              currentExerciseTemplate.repRange &&
              currentExerciseTemplate.repRange.min
                ? currentExerciseTemplate.repRange.min
                : ""
            }
            maxInitialValue={
              currentExerciseTemplate &&
              currentExerciseTemplate.repRange &&
              currentExerciseTemplate.repRange.max
                ? currentExerciseTemplate.repRange.max
                : ""
            }
            onSubmit={handleRepRangeSubmit}
            isLoading={isInitialLoading}
          />
        </div>
        <div className="pt-8 px-2 grid grid-cols-[auto,1fr] gap-x-2 items-center">
          <h2 className="text-xs font-medium text-indigo-600 w-fit">
            Advanced Set Programming (Optional)
          </h2>
          <hr className="h-0.5 w-full bg-indigo-600"></hr>
        </div>
        <p className="p-2 pb-0 text-xs font-regular text-gray-500">
          These exercise details are for advanced programs. Only add them if
          they’re part of your plan.
        </p>
        <div className="pt-4 px-2">
          <RestRangeInput
            rest={
              currentExerciseTemplate && currentExerciseTemplate.rest
                ? currentExerciseTemplate.rest
                : {}
            }
            onSubmit={handleRestRangeSubmit}
            isLoading={isInitialLoading}
          />
        </div>
        <div className="pt-4 px-2">
          <TempoInput
            tempo={
              currentExerciseTemplate && currentExerciseTemplate.tempo
                ? currentExerciseTemplate.tempo
                : {}
            }
            onSubmit={handleTempoSubmit}
            isLoading={isInitialLoading}
          />
        </div>
        {/* <div className="py-4 px-2">
          <RPERangeInput
            rpeRange={
              currentExerciseTemplate && currentExerciseTemplate.rpeRange
                ? currentExerciseTemplate.rpeRange
                : undefined
            }
            onSubmit={() => {}}
            isLoading={isInitialLoading}
          />
        </div> */}
        <NotesInput
          notes={currentExerciseTemplate ? currentExerciseTemplate.notes : ""}
          onNotesUpdated={handleNotesSubmit}
          isLoading={isInitialLoading}
        />
      </div>
      <MessageModal
        isOpen={modalState.isOpen}
        onModalClose={() => {
          setModalState({ ...modalState, isOpen: false });
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
    </div>
  );
};

export default ExerciseTemplate;
